/* eslint-disable no-param-reassign */
import { Map, OrderedMap, fromJS } from 'immutable';
import lodash from 'lodash';

import { getDecimalGradeFromPercent, getPercentGradeFromDecimal } from 'bundles/author-common/utils/GradeUtils';
import ItemTypes from 'bundles/author-course/constants/ItemTypes';
import DATA_MAP_PATHS from 'bundles/author-gradebook/constants/dataMapPaths';
import defaultLearnerColumnConfigs from 'bundles/author-gradebook/constants/learnerColumnConfigs';
import { decrementChar, incrementColumnReference } from 'bundles/author-gradebook/utils/formulaParsing';

import _t from 'i18n!nls/author-gradebook';

const maxDecimalPrecision = 6;

export const formatSingleUserToHOTFormat = (userGradebook: $TSFixMe) => {
  const formattedUser = userGradebook;
  const courseGradeAction = formattedUser.courseGradeOverrideAction?.courseGradeOverrideAction;
  if (courseGradeAction?.definition?.overallGrade) {
    courseGradeAction.definition.grade = getPercentGradeFromDecimal(
      courseGradeAction.definition.overallGrade,
      maxDecimalPrecision
    );
    courseGradeAction.definition.overallGrade = getPercentGradeFromDecimal(
      courseGradeAction.definition.overallGrade,
      maxDecimalPrecision
    );
  }
  lodash.entries(formattedUser.itemOutcomeActions).forEach(([itemId, itemActions]) => {
    // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
    ((itemActions && itemActions.itemOutcomeActions) ?? []).forEach((itemAction) => {
      if (itemAction && itemAction.definition && itemAction.definition.grade) {
        itemAction.definition.grade = getPercentGradeFromDecimal(itemAction.definition.grade, maxDecimalPrecision);
      }
    });
  });
  return fromJS(formattedUser);
};

/**
 * toHOTFormat - transforms backend data to a HOT-compatible format
 * @param {Object} data - BE data
 * @param {Array} itemNames - gradebook itemNames
 * @param {Object} gradedAssignmentGroups - gradebook gradedAssignmentGroups
 * @param {OrderedMap} HOT-compatible data immutable OrderedMap with the structure:
 * { userName:string, courseGrade:Map, courseGradeOverrideAction:Map, itemGrades:OrderedMap itemOutcomeOverrideActions:Map }
 */
export const toHOTFormat = (
  data: $TSFixMe,
  itemNames = [],
  gradedAssignmentGroups: $TSFixMe,
  learnerColumnConfigs = defaultLearnerColumnConfigs
) => {
  // @ts-expect-error ts-migrate(2350) FIXME: Only a void function can be called with the 'new' ... Remove this comment to see the full error message
  let tableMap = new OrderedMap();
  const {
    bulkImportExternalId,
    courseGradeOverrideAction,
    courseGradeOverrideUpdate,
    email,
    gradedAssignmentGroupGradesPath,
    itemOutcomeActions,
    itemGrades,
    liveCourseGradeOutcomePath,
    originalCourseGradeOutcomePath,
    overrideCourseGradeOutcomePath,
    overrideCourseGradeExplanationPath,
  } = DATA_MAP_PATHS;
  const noUserName = _t('(No Name)');

  // convert all decimal grades to percentage values
  lodash
    .entries(data)
    .sort(([userIdA, userA], [userIdB, userB]) =>
      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      (userA.userName ?? noUserName).localeCompare(userB.userName ?? noUserName)
    )
    .forEach(([userId, user]) => {
      learnerColumnConfigs.forEach((column) => {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        tableMap = tableMap.setIn([userId, column.dataKey], user[column.dataKey]);
      });
      // @ts-expect-error ts-migrate(2350) FIXME: Only a void function can be called with the 'new' ... Remove this comment to see the full error message
      tableMap = tableMap.setIn([userId, 'courseGrade'], new Map());

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      if (user.bulkImportExternalId) {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        tableMap = tableMap.setIn([userId, bulkImportExternalId], user.bulkImportExternalId);
      }

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      tableMap = tableMap.setIn([userId, 'isViewAsLearnerGrade'], user.isViewAsLearnerGrade);

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      if (user.email) {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        tableMap = tableMap.setIn([userId, email], user.email);
      }

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      if (user.courseGrade) {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        if (user.courseGrade.originalCourseOutcome) {
          tableMap = tableMap.setIn(
            [userId, ...originalCourseGradeOutcomePath],
            // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
            getPercentGradeFromDecimal(user.courseGrade.originalCourseOutcome.courseGrade, maxDecimalPrecision)
          );
        }

        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        if (user.courseGrade.liveCourseOutcome) {
          tableMap = tableMap.setIn(
            [userId, ...liveCourseGradeOutcomePath],
            // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
            getPercentGradeFromDecimal(user.courseGrade.liveCourseOutcome.courseGrade, maxDecimalPrecision)
          );
        }

        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        if (user.courseGrade.overrideCourseOutcome) {
          // map existing override verifiedGrade/overallGrade wording to 'courseGrade' wording used in gradebook
          tableMap = tableMap.setIn(
            [userId, ...overrideCourseGradeOutcomePath],
            getPercentGradeFromDecimal(
              // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
              user.courseGrade.overrideCourseOutcome.verifiedGrade ??
                // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
                user.courseGrade.overrideCourseOutcome.overallGrade,
              maxDecimalPrecision
            )
          );
          // get the explanation for the grade change
          tableMap = tableMap.setIn(
            [userId, ...overrideCourseGradeExplanationPath],
            // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
            user.courseGrade.overrideCourseOutcome?.explanation
          );
        }
      }

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      if (user.courseGradeOverrideAction?.courseGradeOverrideAction?.typeName === courseGradeOverrideUpdate) {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        user.courseGradeOverrideAction.courseGradeOverrideAction.definition.grade = getPercentGradeFromDecimal(
          // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
          user.courseGradeOverrideAction.courseGradeOverrideAction.definition.overallGrade,
          maxDecimalPrecision
        );
      }

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      tableMap = tableMap.setIn([userId, courseGradeOverrideAction], fromJS(user.courseGradeOverrideAction ?? {}));
      tableMap = tableMap.setIn(
        [userId, courseGradeOverrideAction, courseGradeOverrideAction],
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        fromJS(user.courseGradeOverrideAction?.courseGradeOverrideAction ?? {})
      );

      // @ts-expect-error ts-migrate(2350) FIXME: Only a void function can be called with the 'new' ... Remove this comment to see the full error message
      tableMap = tableMap.setIn([userId, itemOutcomeActions], new Map());
      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      lodash.entries(user.itemOutcomeActions).forEach(([itemId, itemActions]) => {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        ((itemActions && itemActions.itemOutcomeActions) ?? []).forEach((itemAction) => {
          if (itemAction && itemAction.definition && itemAction.definition.grade) {
            itemAction.definition.grade = getPercentGradeFromDecimal(itemAction.definition.grade, maxDecimalPrecision);
          }
        });

        tableMap = tableMap.setIn([userId, itemOutcomeActions, itemId], fromJS(itemActions)); // set createdAt and createdBy
        tableMap = tableMap.setIn(
          [userId, itemOutcomeActions, itemId, itemOutcomeActions],
          // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
          fromJS(itemActions && itemActions.itemOutcomeActions)
        );
      });

      // @ts-expect-error ts-migrate(2350) FIXME: Only a void function can be called with the 'new' ... Remove this comment to see the full error message
      tableMap = tableMap.setIn([userId, itemGrades], new OrderedMap());
      itemNames.forEach(({ itemId, itemName }) => {
        // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
        const itemGrade = user.itemGrades[itemId] ?? {};
        if (!lodash.isEmpty(itemGrade)) {
          if (itemGrade.originalOutcome) {
            itemGrade.originalOutcome.grade = getPercentGradeFromDecimal(
              itemGrade.originalOutcome.grade,
              maxDecimalPrecision
            );
          }
          if (itemGrade.liveOutcome) {
            itemGrade.liveOutcome.grade = getPercentGradeFromDecimal(itemGrade.liveOutcome.grade, maxDecimalPrecision);
          }
          if (itemGrade.overrideOutcome) {
            itemGrade.overrideOutcome.grade = getPercentGradeFromDecimal(
              itemGrade.overrideOutcome.grade,
              maxDecimalPrecision
            );
          }
          // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
          itemGrade.itemName = itemNames.find((item) => item.itemId === itemId).itemName;

          // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
          const prePenaltyGrade = user.attemptGrades?.[itemId]?.prePenaltyGrade;

          if (!lodash.isNil(prePenaltyGrade)) {
            itemGrade.prePenaltyGrade = getPercentGradeFromDecimal(prePenaltyGrade, maxDecimalPrecision);
          }
        }
        tableMap = tableMap.setIn([userId, itemGrades, itemId], fromJS(itemGrade));
      });

      // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
      if (user.gradedAssignmentGroupGrades) {
        const gagIds = Object.keys(gradedAssignmentGroups);
        gagIds.forEach((gagId) => {
          // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
          const rawGagGrade = user.gradedAssignmentGroupGrades[gagId];
          const gagGrade =
            rawGagGrade !== undefined ? getPercentGradeFromDecimal(rawGagGrade, maxDecimalPrecision) : rawGagGrade;

          tableMap = tableMap.setIn([userId, gradedAssignmentGroupGradesPath, gagId], fromJS(gagGrade));
        });
      }
    });

  return tableMap;
};

/**
 * toBEFormat - reverse transform of toHOTFormat
 * @param {Object} data - HOT data
 * @param {Object} BE-compatible data object
 */
export const toBEFormat = (data: $TSFixMe) => {
  const formatted = JSON.parse(JSON.stringify(data));
  const { courseGradeOverrideUpdate } = DATA_MAP_PATHS;

  // convert all percentage grades back to decimals for BE
  lodash.each(formatted.userGradebooks, (user) => {
    if (
      user.courseGradeOverrideAction &&
      user.courseGradeOverrideAction.courseGradeOverrideAction.typeName === courseGradeOverrideUpdate
    ) {
      const decimalGrade = getDecimalGradeFromPercent(
        user.courseGradeOverrideAction.courseGradeOverrideAction.definition.grade,
        maxDecimalPrecision
      );

      user.courseGradeOverrideAction.courseGradeOverrideAction.definition.grade = decimalGrade;
      user.courseGradeOverrideAction.courseGradeOverrideAction.definition.overallGrade = decimalGrade;
      user.courseGradeOverrideAction.courseGradeOverrideAction.definition.verifiedGrade = decimalGrade;
    }
    lodash.each(user.itemOutcomeActions, (itemActions) => {
      (itemActions?.itemOutcomeAction ?? []).forEach((itemAction: $TSFixMe) => {
        if (itemAction?.definition?.grade) {
          itemAction.definition.grade = getDecimalGradeFromPercent(itemAction.definition.grade, maxDecimalPrecision);
        }
      });
    });
  });

  return formatted;
};

/**
 * toDefaultTableFormat - transforms store HOT-compatible format into row and column headers and data for the default table
 * @param {Object} data - HOT-compatible Immutable data object
 * @param {Object} itemNames - array of gradebook item names
 * @param {Object} {colHeaders, formattedData }
 */
export const toDefaultTableFormat = (
  data: $TSFixMe,
  itemNames = [],
  learnerColumns: $TSFixMe,
  gradedAssignmentGroups = {}
) => {
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'itemName' does not exist on type 'never'... Remove this comment to see the full error message
  const itemNameHeaders = itemNames.map((item) => item.itemName);
  const gradedAssignmentGroupHeaders = Object.keys(gradedAssignmentGroups).map(
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    (gagId) => gradedAssignmentGroups[gagId].name
  );
  const learnerNameHeaders = learnerColumns.map(({ getLabel }: $TSFixMe) => getLabel());
  const colHeaders = [...learnerNameHeaders, 'Present Grade', ...itemNameHeaders, ...gradedAssignmentGroupHeaders];

  const {
    courseGradeOverrideAction,
    courseGradeOverrideDelete,
    overrideCourseGradeOutcomePath,
    originalCourseGradeOutcomePath,
    itemOutcomeOverrideDelete,
    courseGradeOverrideActionGradePath,
    itemGrades,
    itemOriginalOutcomeGradePath,
    itemOverrideOutcomeGradePath,
    gradedAssignmentGroupGradesPath,
  } = DATA_MAP_PATHS;

  const formattedData = data
    .valueSeq()
    .map((learner: $TSFixMe) => {
      const learnerIdentifierColumnValues = learnerColumns.map(({ dataKey }: $TSFixMe) => learner.get(dataKey));
      const courseGradeAction = learner.getIn([courseGradeOverrideAction, courseGradeOverrideAction, 'typeName']);
      let gradedAssignmentGroupGrades = [];

      let courseGrade;
      if (courseGradeAction !== courseGradeOverrideDelete) {
        courseGrade =
          learner.getIn(courseGradeOverrideActionGradePath) ??
          learner.getIn(overrideCourseGradeOutcomePath) ??
          learner.getIn(originalCourseGradeOutcomePath);
      } else {
        courseGrade = learner.getIn(originalCourseGradeOutcomePath);
      }

      const gradedAssignmentGroupData = learner.get(gradedAssignmentGroupGradesPath);

      if (gradedAssignmentGroupData) {
        // @ts-expect-error ts-migrate(7031) FIXME: Binding element 'gagId' implicitly has an 'any' ty... Remove this comment to see the full error message
        gradedAssignmentGroupGrades = gradedAssignmentGroupData.entrySeq().map(([gagId, gagGrade]) => {
          return gagGrade;
        });
      }

      return [
        ...learnerIdentifierColumnValues,
        courseGrade,
        ...learner
          .get(itemGrades)
          .entrySeq()
          // @ts-expect-error ts-migrate(7031) FIXME: Binding element 'itemId' implicitly has an 'any' t... Remove this comment to see the full error message
          .map(([itemId, itemGrade]) => {
            const itemOutcomeActions = learner.getIn(['itemOutcomeActions', itemId, 'itemOutcomeActions']);
            if (
              itemOutcomeActions &&
              !itemOutcomeActions.find((action: $TSFixMe) => action.get('typeName') === itemOutcomeOverrideDelete)
            ) {
              const itemOverrideUpdateAction = itemOutcomeActions.find(
                (action: $TSFixMe) => action.get('typeName') === 'itemOutcomeOverrideUpdate'
              );
              return (
                (itemOverrideUpdateAction && itemOverrideUpdateAction.getIn(['definition', 'grade'])) ??
                itemGrade.getIn(itemOverrideOutcomeGradePath) ??
                itemGrade.getIn(itemOriginalOutcomeGradePath)
              );
            } else {
              return itemGrade.getIn(itemOverrideOutcomeGradePath) ?? itemGrade.getIn(itemOriginalOutcomeGradePath);
            }
          }),
        ...gradedAssignmentGroupGrades,
      ];
    })
    .toJS();

  return {
    colHeaders,
    formattedData,
  };
};

/**
 * toAdvancedTableFormat - transforms store HOT-compatible format into column headers and data for the advanced table
 * @param {Object} data - HOT-compatible data object
 * @param {Object} itemNames - array of gradebook item names
 * @param {Object} learnerColumns {colHeaders, formattedData}
 * @param {boolean} enablePrePenaltyGrades
 */
export const toAdvancedTableFormat = (
  data: $TSFixMe,
  itemNames = [],
  learnerColumns: $TSFixMe,
  enablePrePenaltyGrades: boolean
) => {
  const {
    courseGradeOverrideAction,
    courseGradeOverrideDelete,
    overrideCourseGradeOutcomePath,
    originalCourseGradeOutcomePath,
    liveCourseGradeOutcomePath,
    courseGradeOverrideActionGradePath,
    courseGradeOverrideActionFormulaPath,
    itemGrades,
    itemOriginalOutcomeGradePath,
    itemLiveOutcomeGradePath,
  } = DATA_MAP_PATHS;

  const learnerNameHeaders = learnerColumns.map(({ getLabel }: $TSFixMe) => getLabel());
  // convert Immutable data structures from the store to the normal JS data structures handsontable expects
  let lastReference = decrementChar('A', 1);
  let colHeaders = itemNames.reduce(
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    (nestedHeaders, item) => {
      const newMarkerLevel = enablePrePenaltyGrades
        ? [
            ...nestedHeaders[0],
            incrementColumnReference(lastReference, 1),
            incrementColumnReference(lastReference, 2),
            incrementColumnReference(lastReference, 3),
            incrementColumnReference(lastReference, 4),
          ]
        : [
            ...nestedHeaders[0],
            incrementColumnReference(lastReference, 1),
            incrementColumnReference(lastReference, 2),
            incrementColumnReference(lastReference, 3),
          ];
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'itemType' does not exist on type 'never'... Remove this comment to see the full error message
      const itemType = lodash.values(ItemTypes).find((type) => type.typeName === item.itemType) ?? '';

      const itemPrefix =
        itemType &&
        itemType.getPrefixTranslation({
          // @ts-expect-error ts-migrate(2769) FIXME: Property 'isTeamSubmissionEnabled' does not exist on type 'never'... Remove this comment to see the full error message
          isTeamAssignment: item.isTeamSubmissionEnabled,
        });

      // @ts-expect-error ts-migrate(2339) FIXME: Property 'itemName' does not exist on type 'never'... Remove this comment to see the full error message
      const label = `${itemPrefix}: ${item.itemName}`;

      lastReference = enablePrePenaltyGrades
        ? incrementColumnReference(lastReference, 4)
        : incrementColumnReference(lastReference, 3);

      const newItemNameLevel = enablePrePenaltyGrades
        ? [...nestedHeaders[1], { label, colspan: 4 }]
        : [...nestedHeaders[1], { label, colspan: 3 }];

      const newDetailLevel = enablePrePenaltyGrades
        ? [...nestedHeaders[2], 'Pre Penalty Grade', 'Raw Grade', 'Live Grade', 'Override']
        : [...nestedHeaders[2], 'Raw Grade', 'Live Grade', 'Override'];

      return [newMarkerLevel, newItemNameLevel, newDetailLevel];
    },
    [[], [], []]
  );

  /* Due to a limitation in ordering headers in Handsontable V8+, the widest header needs to be first in the array.
     This array is in a different order to how it's displayed visually. The assignment header is rendered at the top and then flipped with CSS.
  */
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'any[][]' is not assignable to type 'never'.
  colHeaders = [
    [{ label: '', colspan: learnerNameHeaders.length }, { label: 'Present Grade', colspan: 3 }, ...colHeaders[1]],
    [
      ...colHeaders[0],
      incrementColumnReference(lastReference, 1),
      incrementColumnReference(lastReference, 2),
      incrementColumnReference(lastReference, 3),
      incrementColumnReference(lastReference, 4),
      incrementColumnReference(lastReference, 5),
    ],
    [...learnerNameHeaders, 'Raw Grade', 'Live Grade', 'Override', ...colHeaders[2]],
  ];

  const formattedData = data
    .valueSeq()
    .map((learner: $TSFixMe) => {
      const learnerIdentifierColumnValues = learnerColumns.map(({ dataKey }: $TSFixMe) => learner.get(dataKey));
      const courseGradeAction = learner.getIn([courseGradeOverrideAction, 'typeName']);
      const courseGradeOverride =
        courseGradeAction !== courseGradeOverrideDelete
          ? learner.getIn(courseGradeOverrideActionFormulaPath) ?? // override formula
            learner.getIn(courseGradeOverrideActionGradePath) ?? // OR override grade
            learner.getIn(overrideCourseGradeOutcomePath)
          : null;

      const courseGrade = [
        learner.getIn(originalCourseGradeOutcomePath), // raw
        learner.getIn(liveCourseGradeOutcomePath), // live
        courseGradeOverride,
      ];

      return [
        ...learnerIdentifierColumnValues,
        ...courseGrade,
        ...learner
          .get(itemGrades)
          .entrySeq()
          // @ts-expect-error ts-migrate(7031) FIXME: Binding element 'itemId' implicitly has an 'any' t... Remove this comment to see the full error message
          .reduce((cells: $TSFixMe, [itemId, itemGrade]) => {
            const itemOutcomeActions = learner.getIn(['itemOutcomeActions', itemId, 'itemOutcomeActions']);
            const itemOverrideUpdateAction = itemOutcomeActions?.find(
              (action: $TSFixMe) => action.get('typeName') === 'itemOutcomeOverrideUpdate'
            );
            const override = itemOverrideUpdateAction ? itemOverrideUpdateAction.getIn(['definition', 'grade']) : null;

            const prePenaltyGrade = itemGrade.get('prePenaltyGrade');
            const rawGrade = itemGrade.getIn(itemOriginalOutcomeGradePath);
            const liveGrade = itemGrade.getIn(itemLiveOutcomeGradePath);

            return enablePrePenaltyGrades
              ? [...cells, prePenaltyGrade, rawGrade, liveGrade, override]
              : [...cells, rawGrade, liveGrade, override];
          }, []),
      ];
    })
    .toJS();
  return {
    colHeaders,
    formattedData,
  };
};
